export enum MeetingPrepStatus {
  NOT_STARTED = "NOT_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  READY = "READY",
}

export enum MeetingPrepGoalOrigin {
  USER = "USER",
  AI_SUGGESTED = "AI_SUGGESTED",
  AI_EDITED = "AI_EDITED",
}

/**
 * Dispatch handshake with skrift-bg-agent's bulk talking-points graph (SKR-756).
 * The app is the only writer of REQUESTED; the agent owns every other value and
 * claims work with a compare-and-swap on REQUESTED -> GENERATING.
 */
export enum TalkingPointsStatus {
  IDLE = "IDLE",
  REQUESTED = "REQUESTED",
  GENERATING = "GENERATING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

/** Statuses the FE renders as "generating"; a refresh request is a no-op here. */
export const TALKING_POINTS_IN_FLIGHT_STATUSES: readonly TalkingPointsStatus[] =
  [TalkingPointsStatus.REQUESTED, TalkingPointsStatus.GENERATING];

/** The two statuses the rep should see as "the AI is working on it". */
export function isTalkingPointsGenerating(
  status: TalkingPointsStatus
): boolean {
  return TALKING_POINTS_IN_FLIGHT_STATUSES.includes(status);
}

export enum TalkingPointOrigin {
  AI_GENERATED = "AI_GENERATED",
  AI_EDITED = "AI_EDITED",
  USER_CREATED = "USER_CREATED",
}

/**
 * Who ticked a talking point: the rep on the live checklist, or the bg-agent's
 * coverage pass reading the call transcript (SKR-859). NULL on rows checked
 * before the column existed; the UI reads NULL as a rep check.
 */
export enum TalkingPointCheckedOrigin {
  USER = "USER",
  AI = "AI",
}

export enum TalkingPointSourceType {
  GROWTH_SIGNAL = "GROWTH_SIGNAL",
  RISK_SIGNAL = "RISK_SIGNAL",
  GOAL = "GOAL",
  ACTION_ITEM = "ACTION_ITEM",
  FEEDBACK = "FEEDBACK",
  ACTIVITY = "ACTIVITY",
}

/**
 * Machine-readable discriminator carried in every `prep-errors.ts` JSON body.
 * `PrepReadOnlyError` and `TalkingPointsStaleError` both answer 409, but they
 * are not the same situation: one is unrecoverable (the call ended, nothing
 * the rep does will unfreeze the prep) and the other is a reconcile-and-retry.
 * Branching on status alone cannot tell them apart, so the FE branches on this
 * instead — `ApiError.code` already carries it end to end via `apiClient`.
 */
export enum PrepErrorCode {
  PREP_NOT_FOUND = "PREP_NOT_FOUND",
  PREP_READ_ONLY = "PREP_READ_ONLY",
  TALKING_POINTS_STALE = "TALKING_POINTS_STALE",
  TALKING_POINTS_REORDER_MISMATCH = "TALKING_POINTS_REORDER_MISMATCH",
}
